import AutoCompleteAllowAdd from '@/components/autoCompleteAllowAdd';
import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import { BREAKPOINT } from '@/constants/enum';
import { checkShowErrorInline, handleToastMutation } from '@/helpers';
import { uniq } from '@/helpers/string';
import useShippingMethodSeparator from '@/hooks/useShippingMethodSeparator';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { IOptions } from '@/types';
import { Button } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

interface IProps {
  isRequired?: boolean;
  setSelectedOptions: (value: IOptions[]) => void;
  selectedOptions: IOptions[];
  defaultOptions: IOptions[];
  customSetError?: (msg: string) => void;
}

export default function SelectShippingMethod({
  selectedOptions,
  setSelectedOptions,
  defaultOptions,
  customSetError,
  ...props
}: IProps) {
  const shippingMethodSeparator = useShippingMethodSeparator();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const isSkip = useSelector(isSkipApiSelector);
  const { data: optionsShipping } = apiCaller.useGetShopShippingMethodsQuery(undefined, { skip: isSkip });
  const { data: optionsShippingUsed } = apiCaller.useGetShopShippingMethodsUsedQuery(undefined, { skip: isSkip });
  const [sync, { isLoading: isLoadingSync }] = apiCaller.useSyncProductAndCollectionMutation();
  const [options, setOptions] = useState<IOptions[]>([]);

  const handleSync = () => {
    if (isSkip) return;
    sync().then((res) => {
      const checkError = checkShowErrorInline(res);
      if (!checkError.status) {
        dispatch(toastSlice.actions.handleToast(handleToastMutation({ data: { message: 'Sync successfully', code: 200 } })));
      }
    });
  };

  useEffect(() => {
    if (optionsShipping?.data) {
      const listOptionsUsed = (optionsShippingUsed?.data || []).map((option) => option.split(shippingMethodSeparator)).flat();
      const newOptions = uniq([...optionsShipping?.data, ...listOptionsUsed]).map((item) => ({ value: item, label: item }));
      if (defaultOptions?.length) {
        newOptions.push(...defaultOptions);
      }
      setOptions(uniq(newOptions.map((i) => ({ value: i.value, label: i.label }))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultOptions), JSON.stringify(optionsShipping?.data)]);

  return (
    <CustomCard
      required={props.isRequired}
      hasDroplist
      title={'Shipping method'}
      titleContentRight={
        <div className={`${isMobile ? 'mt-4' : ''}`}>
          <Button loading={isLoadingSync} onClick={handleSync}>
            Sync from Shopify
          </Button>
        </div>
      }
      isResponsiveAction
    >
      <div className="mb-8">
        <RegularText>
          Select shipping method, or Enter name if not fully synced. Name should match shipping method set in Shopify settings
        </RegularText>
      </div>
      <AutoCompleteAllowAdd
        allowMultiple
        label=""
        options={options}
        setOptions={setOptions}
        tag={selectedOptions}
        selectedOptions={selectedOptions.map((item) => {
          if (typeof item === 'string') return item;
          return item.value;
        })}
        setSelectedOptions={setSelectedOptions}
        allowAdd
        requiredIndicator={true}
        clearInputWhenAdd
        removeSeparator={shippingMethodSeparator}
        customSetError={customSetError}
      />
    </CustomCard>
  );
}
